import { TemplateType } from '@/types/boot';
import { TEMPLATE_CLASSES } from './styles';

/**
 * Tag describing how the descriptor decides the *primary* payment method
 * surfaced to the user. Consumed by leaves/analytics when behavior needs to
 * branch on intent rather than on the template enum.
 */
export type PrimaryPaymentMethodSource = 'first-available' | 'device-express' | 'be-last-method' | 'config-single';

/**
 * Per-template style record. Each surface is a plain Tailwind class string
 * declared once per template — no branching, no getters. Adding a new
 * template means adding one entry to {@link TemplateStyleConfigurations}.
 *
 * Components MUST read these via `template.styles.X`; they MUST NOT branch
 * on `template.type` or on a feature flag to pick a class string.
 */
export interface TemplateStyles {
  /** Outer Details section, appended to `DETAILS_STYLES.base`. */
  detailsContainer: string;
  /** Inner wrapper around DetailsWrapper + TotalPrice. */
  detailsWrapper: { mobile: string; desktop: string };
  /**
   * Inner products + price-breakdown stack inside DetailsWrapper.
   *
   * DEFAULT keeps the legacy rhythm (no row gap). PREFERRED and SINGLE
   * add an explicit row gap so the Subtotal row doesn't sit flush
   * against the products list. We branch on this style key instead of
   * `features.compactDetailsLayout` because the two don't align:
   * `compactDetailsLayout` is `true` only for SINGLE, but the row-gap
   * treatment is wanted on both SINGLE and PREFERRED.
   */
  detailsBodyStack: string;
  /**
   * Collapsible region for the purchased-items list. `open`/`closed` drive the
   * outer grid-rows shell; `content` is the always-present inner wrapper that
   * owns the row gap (kept constant so the collapse stays smooth).
   */
  detailsCollapsible: { open: string; closed: string; content: string };
  /** Outer Payment section, appended to `PAYMENT_STYLES.base`. */
  paymentContainer: string;
}

export interface TemplateFeatures {
  showPaymentMethods: boolean;
  showExpressCheckout: boolean;
  autoSelectPaymentMethod: boolean;
  showDesktopViewer: boolean;
  centerContentOnDesktop: boolean;
  defaultDetailsOpen: boolean;
  showXButton: boolean;
  /**
   * Offer the "Back to Store" link under the pay button. Only consulted in
   * redirect mode (`?mode=redirect`), where the checkout owns the whole tab:
   * there is no overlay to dismiss, so the X is dropped and this link becomes
   * both the only way out and the only remaining source of `status=cancel`.
   *
   * SINGLE only — that is the template the redirect-mode design covers. Other
   * templates opened with `?mode=redirect` currently have no exit control.
   */
  showBackToStoreLink: boolean;
  /**
   * Whether the publisher hero background image is allowed to paint behind the
   * template's Details area, per viewport.
   *
   * Split per viewport because SINGLE_PAYMENT_METHOD on desktop intentionally
   * renders as a centered compact card on plain white — the hero image is
   * inappropriate there, but is still desired on mobile where the same view
   * fills the full screen. Keeping a single `boolean` flag forced an all-or-
   * nothing choice and required `OnePaymentView` to branch on `isMobile`
   * itself, which we explicitly forbid by the leaves-don't-know-template rule.
   *
   * Final visibility still depends on the publisher having an image configured
   * (see `hasPublisherHaveBackgroundImage`).
   */
  showBackground: { mobile: boolean; desktop: boolean };
  /** Single-column collapsible details body (matches the existing `one_payment` layout). */
  compactDetailsLayout: boolean;
  /** Whether the items toggle can appear. Final visibility also requires mobile OR `compactDetailsLayout`. */
  showItemsToggle: boolean;
  /** Drop the "Payment via X" header inside express forms and surface critical load errors. */
  compactExpressForms: boolean;
  /**
   * Render the per-WALLET APM header above the form's primary CTA:
   *  - "Payment via Google Pay" + redirect message (GooglePayForm)
   *  - "Payment via PayPal"     + redirect message (PaypalForm, also gated on `shouldShowPayPalAsApm`)
   * When false, the wallet form's CTA stands alone. Decoupled from `compactExpressForms`
   * so PREFERRED can keep verbose forms but still hide the header — in PREFERRED /
   * SINGLE the user has already chosen the wallet, so the label is redundant noise
   * above an already-self-explanatory CTA. Apple Pay has no equivalent header today,
   * so it's not affected. NOTE: the credit-card title is controlled separately by
   * `showCardFormHeader` — it's wanted even when card is the primary CTA.
   */
  showApmFormHeader: boolean;
  /**
   * Render the "Pay with Debit or Credit Card" title above the card form
   * (`CreditCardForm`). Unlike the wallet APM headers, this stays on even when
   * card is the primary/auto-selected method (SINGLE / PREFERRED): the title
   * anchors the manual card-entry form and reads as a section label, not
   * redundant noise.
   */
  showCardFormHeader: boolean;
  /** Mobile-only express-first body layout (single CTA + Use Other Method link). */
  mobileOnlyExpressFirstLayout: boolean;
  /**
   * Keep the WHOLE screen on the skeleton until the PSP confirms the native
   * wallet is actually available, instead of painting the view and letting it
   * swap to DEFAULT mid-flight.
   *
   * When `true`, `PreferredPaymentView` mounts the real content (incl. the
   * wallet's PSP probe) hidden under a full-screen skeleton overlay. The
   * existing readiness signal (Stripe ECE `onReady` → `availablePaymentMethods`,
   * Adyen `isAvailable()`) reveals it on "available"; on "unavailable" — or if
   * the signal never arrives within `EXPRESS_WALLET_READY_TIMEOUT_MS` — the
   * template falls back via `onExpressWalletUnavailable` (PREFERRED → DEFAULT)
   * before anything wallet-specific is ever shown.
   *
   * This kills the FTD / last-payment flash where the player briefly sees an
   * Apple Pay CTA that then disappears into the full method picker. Only
   * PREFERRED critically depends on the native wallet (and only its Apple Pay
   * form participates in the reveal handshake today), so only it defers;
   * DEFAULT/SINGLE have no template swap to "flash" against.
   */
  deferExpressWalletReveal: boolean;
  /** Show the "Use other payment method" secondary link below the primary CTA. */
  secondaryUseOtherPaymentMethodLink: boolean;
  /** Block DEFAULT-style express wallets until ZIP/postal code is collected for tax. */
  blockExpressWalletsForZipCode: boolean;
  /**
   * Hide the ZIP/postal tax input when the shopper already has a persisted ZIP
   * on file (see `hasStoredZipCode`). Enabled for PREFERRED (last-payment): a
   * returning depositor's ZIP is already known, so re-asking for it in the
   * fast one-click flow is redundant. DEFAULT/SINGLE keep the field so US/CA
   * shoppers can always view/correct it (the historical behaviour).
   *
   * Only takes effect for US/CA shoppers — `shouldShowZipCodeFieldForTaxCalculation`
   * still gates whether the field is relevant at all.
   */
  hidePrefilledZipCode: boolean;
  /**
   * Hide the email input when the shopper already has a *valid* email on file
   * (`isEmailValid(customer.email)`). Enabled for PREFERRED (FTD /
   * last-payment): those flows optimise for one-tap so re-asking for a known-good
   * email is redundant noise; an invalid persisted email still surfaces the
   * field so the shopper can correct it. DEFAULT/SINGLE keep the historical
   * behaviour of hiding on any truthy `customer.email` (still gated by the
   * `CHECKOUT_ENFORCE_CUSTOMER_EMAIL` FF and only if a caller actually passes
   * `customer` — the field otherwise stays visible so nothing regresses).
   *
   * ACDEV-27034.
   */
  hidePrefilledEmail: boolean;
  /** Render blocked ZIP/postal feedback on the ZIP input instead of as a separate prompt error. */
  showBlockedZipCodeErrorInInput: boolean;
  /**
   * When a payment method has both an express-button implementation and a
   * legacy PaymentElement / tile implementation, prefer the express button.
   * Set `true` on templates where the primary CTA IS the selected wallet
   * (PREFERRED today) so the shopper gets a one-click, wallet-hosted flow
   * instead of the PaymentElement + hosted-redirect-tab detour.
   *
   * Consumers today:
   *   - {@link AmazonPayForm} — flag `true` + Stripe provider →
   *     `AmazonPayStripeForm` (Stripe Express Checkout Element); otherwise
   *     falls back to `GenericPayForm` (PaymentElement + redirect tab).
   *
   * Apple Pay / Google Pay / Link don't consult this flag — they have no
   * legacy fallback path and always render as express buttons via their
   * dedicated forms.
   */
  preferExpressButton: boolean;
  /**
   * On phones, surface ONLY the device-native express wallet (Apple Pay on
   * iOS, Google Pay on Android) in the express row — never a second wallet
   * (e.g. Google Pay on iPhone) nor PayPal. Desktop/tablet (`DeviceType.WEB`)
   * is never restricted regardless of this flag; the device half of the
   * decision lives in `shouldRestrictExpressToNativeWallet`.
   *
   * True for the templates that render a MULTI-wallet express row (DEFAULT,
   * EMBEDDED_VIEW). PREFERRED already resolves a single device CTA and SINGLE has
   * no express row, so neither needs the restriction.
   */
  restrictExpressToNativeWallet: boolean;
  /**
   * Let the payment column hug its content and pin the Footer to the bottom of
   * the scroll viewport, instead of spreading form and Footer to opposite edges
   * of a full-height column.
   *
   * DEFAULT/SINGLE/PREFERRED use `justify-between` on a `h-full` section, which
   * pins the Footer to the bottom of the viewport and leaves dead space between
   * it and the Pay button whenever the content is short. That reads fine behind
   * those templates' publisher hero, but EMBEDDED_VIEW is a plain white sheet where
   * the gap looks like a rendering bug — so the Footer follows the form
   * directly, and only becomes sticky once the column actually overflows.
   */
  footerFollowsContent: boolean;
  /**
   * Float the payment error directly above the Footer instead of pinning it to
   * the bottom edge of the viewport.
   *
   * Both placements are overlays that leave the layout untouched
   * (ACDEV-27000); they differ in what they are anchored to. EMBEDDED_VIEW's
   * iframe is sized to the content height, so "bottom of the viewport" is the
   * bottom of the Footer and the toast lands on top of it. Anchoring to the
   * Footer's top edge instead puts the banner over the end of the form, where
   * there is room for it.
   */
  errorFollowsContent: boolean;
  /** Intent tag for primary payment method selection. */
  primaryPaymentMethodSource: PrimaryPaymentMethodSource;
}

export const TemplateConfigurations: Record<TemplateType, TemplateFeatures> = {
  [TemplateType.DEFAULT]: {
    showPaymentMethods: true,
    showExpressCheckout: true,
    autoSelectPaymentMethod: false,
    showDesktopViewer: true,
    centerContentOnDesktop: false,
    defaultDetailsOpen: false,
    showXButton: true,
    showBackToStoreLink: false,
    showBackground: { mobile: true, desktop: true },
    compactDetailsLayout: false,
    showItemsToggle: true,
    compactExpressForms: false,
    showApmFormHeader: true,
    showCardFormHeader: true,
    mobileOnlyExpressFirstLayout: false,
    deferExpressWalletReveal: false,
    secondaryUseOtherPaymentMethodLink: false,
    blockExpressWalletsForZipCode: true,
    hidePrefilledZipCode: false,
    hidePrefilledEmail: false,
    showBlockedZipCodeErrorInInput: false,
    preferExpressButton: false,
    restrictExpressToNativeWallet: true,
    footerFollowsContent: false,
    errorFollowsContent: false,
    primaryPaymentMethodSource: 'first-available',
  },
  [TemplateType.SINGLE_PAYMENT_METHOD]: {
    showPaymentMethods: false,
    showExpressCheckout: false,
    autoSelectPaymentMethod: true,
    showDesktopViewer: true,
    centerContentOnDesktop: true,
    // Items list starts expanded on SINGLE so the player sees exactly what
    // they're buying up-front (there's only one payment method, so the
    // breakdown is the primary context). The toggle still lets them collapse.
    defaultDetailsOpen: true,
    showXButton: true,
    showBackToStoreLink: true,
    // Mobile keeps the publisher hero behind XButton + Details (matches
    // PREFERRED + DEFAULT). Desktop intentionally omits the hero: the SINGLE
    // view renders as a centered compact card on plain white, where the hero
    // would smear behind a narrow column and read as a layout bug.
    showBackground: { mobile: true, desktop: false },
    compactDetailsLayout: true,
    showItemsToggle: true,
    compactExpressForms: true,
    showApmFormHeader: false,
    showCardFormHeader: true,
    mobileOnlyExpressFirstLayout: false,
    deferExpressWalletReveal: false,
    secondaryUseOtherPaymentMethodLink: false,
    blockExpressWalletsForZipCode: false,
    hidePrefilledZipCode: false,
    hidePrefilledEmail: false,
    showBlockedZipCodeErrorInInput: true,
    preferExpressButton: false,
    restrictExpressToNativeWallet: false,
    footerFollowsContent: false,
    errorFollowsContent: false,
    primaryPaymentMethodSource: 'config-single',
  },
  [TemplateType.PREFERRED_PAYMENT_METHOD]: {
    showPaymentMethods: false,
    showExpressCheckout: false,
    autoSelectPaymentMethod: true,
    showDesktopViewer: false,
    centerContentOnDesktop: true,
    defaultDetailsOpen: true,
    showXButton: true,
    showBackToStoreLink: false,
    // PREFERRED is mobile-first. On desktop `PreferredPaymentView` falls back
    // to `DefaultView` (full hero pane), so we keep desktop enabled too —
    // matches today's behaviour and the desktop fallback's expectations.
    showBackground: { mobile: true, desktop: true },
    compactDetailsLayout: false,
    // Surface the items collapse/expand toggle so the user can hide the
    // products list on returning-depositor + FTD screens. `defaultDetailsOpen`
    // stays true — list starts open, toggle just lets them collapse.
    showItemsToggle: true,
    compactExpressForms: false,
    showApmFormHeader: false,
    showCardFormHeader: true,
    mobileOnlyExpressFirstLayout: true,
    // FTD / last-payment lives or dies on the native wallet, so don't paint the
    // Apple/Google CTA until the PSP confirms it; otherwise fall back to DEFAULT
    // without ever flashing the wallet button. See `deferExpressWalletReveal`.
    deferExpressWalletReveal: true,
    secondaryUseOtherPaymentMethodLink: true,
    blockExpressWalletsForZipCode: false,
    // Returning depositor already has a ZIP on file — don't re-ask in the
    // fast last-payment flow. Only hides when `hasStoredZipCode` is true.
    hidePrefilledZipCode: true,
    // FTD / last-payment templates hide the email input when the persisted
    // email is valid (ACDEV-27034). Invalid emails still surface the field.
    hidePrefilledEmail: true,
    showBlockedZipCodeErrorInInput: true,
    preferExpressButton: true,
    restrictExpressToNativeWallet: false,
    footerFollowsContent: false,
    errorFollowsContent: false,
    primaryPaymentMethodSource: 'device-express',
  },
  // Mobile-only embedded checkout variant. The payment column is byte-identical to
  // DEFAULT (express row → "Or pay another way" → method tiles → form →
  // Footer), so every payment-facing flag mirrors DEFAULT. The whole delta is
  // the top of the screen: `EmbeddedView` swaps DEFAULT's `TopWrapper` hero
  // (publisher image + offer header + products list) for a compact gray
  // `TotalSummaryCard`. The flags that diverge all follow from that plain white
  // sheet: no hero, no items toggle, and the two below (`footerFollowsContent`,
  [TemplateType.EMBEDDED_VIEW]: {
    showPaymentMethods: true,
    showExpressCheckout: true,
    autoSelectPaymentMethod: false,
    showDesktopViewer: true,
    centerContentOnDesktop: false,
    // The summary card starts collapsed (Total row only); the chevron expands
    // to reveal Subtotal / Tax under the Total.
    defaultDetailsOpen: false,
    showXButton: true,
    showBackToStoreLink: false,
    // No publisher hero on either viewport — the embedded screen is a plain
    // white sheet topped by the gray summary card. Desktop is listed for
    // completeness only: `EmbeddedViewTemplate.isEligible` rejects desktop at
    // boot, so DEFAULT (with its hero) runs there.
    showBackground: { mobile: false, desktop: false },
    compactDetailsLayout: false,
    // The products list is dropped entirely — `TotalSummaryCard` owns its own
    // chevron and reveals only the Subtotal / Tax breakdown, so the shared
    // `ItemsDetails` toggle has nothing to open.
    showItemsToggle: false,
    compactExpressForms: false,
    showApmFormHeader: true,
    showCardFormHeader: true,
    mobileOnlyExpressFirstLayout: false,
    deferExpressWalletReveal: false,
    secondaryUseOtherPaymentMethodLink: false,
    blockExpressWalletsForZipCode: true,
    hidePrefilledZipCode: false,
    hidePrefilledEmail: false,
    showBlockedZipCodeErrorInInput: false,
    preferExpressButton: false,
    restrictExpressToNativeWallet: true,
    // The one payment-column flag that diverges from DEFAULT: no publisher hero
    // here, so a Footer floating at the bottom of a plain white sheet with dead
    // space above it reads as broken. Footer follows the form instead.
    footerFollowsContent: true,
    // The iframe is content-sized, so the shared mobile toast (pinned to the
    // viewport bottom) would land on the Footer. Float it above the Footer.
    errorFollowsContent: true,
    primaryPaymentMethodSource: 'first-available',
  },
};

/**
 * Per-template style table. Strings here are the *only* place template
 * visuals live — `src/constants/styles.ts` keeps just shared base
 * classes and provider-specific tokens.
 *
 * To add a new template, add one entry. Components will pick it up
 * automatically via `template.styles.X`.
 */
export const TemplateStyleConfigurations: Record<TemplateType, TemplateStyles> = {
  [TemplateType.DEFAULT]: {
    detailsContainer: TEMPLATE_CLASSES.detailsContainerDefault,
    detailsWrapper: { mobile: '', desktop: TEMPLATE_CLASSES.detailsWrapperDesktopHalfColumn },
    detailsBodyStack: TEMPLATE_CLASSES.detailsBodyStackDefault,
    detailsCollapsible: {
      open: TEMPLATE_CLASSES.collapsibleOpen,
      closed: TEMPLATE_CLASSES.collapsibleClosed,
      content: TEMPLATE_CLASSES.collapsibleContentStandard,
    },
    paymentContainer: TEMPLATE_CLASSES.paymentContainerDefault,
  },
  [TemplateType.SINGLE_PAYMENT_METHOD]: {
    detailsContainer: TEMPLATE_CLASSES.detailsContainerCompact,
    detailsWrapper: {
      mobile: TEMPLATE_CLASSES.detailsWrapperCompactColumn,
      desktop: TEMPLATE_CLASSES.detailsWrapperCompactColumn,
    },
    detailsBodyStack: TEMPLATE_CLASSES.detailsBodyStackSpaced,
    detailsCollapsible: {
      open: TEMPLATE_CLASSES.collapsibleOpen,
      closed: TEMPLATE_CLASSES.collapsibleClosed,
      content: TEMPLATE_CLASSES.collapsibleContentTight,
    },
    paymentContainer: TEMPLATE_CLASSES.paymentContainerCompact,
  },
  [TemplateType.PREFERRED_PAYMENT_METHOD]: {
    detailsContainer: TEMPLATE_CLASSES.detailsContainerCompact,
    detailsWrapper: { mobile: '', desktop: TEMPLATE_CLASSES.detailsWrapperDesktopHalfColumn },
    detailsBodyStack: TEMPLATE_CLASSES.detailsBodyStackSpaced,
    detailsCollapsible: {
      open: TEMPLATE_CLASSES.collapsibleOpen,
      closed: TEMPLATE_CLASSES.collapsibleClosed,
      content: TEMPLATE_CLASSES.collapsibleContentStandard,
    },
    paymentContainer: TEMPLATE_CLASSES.paymentContainerExpressFirst,
  },
  // Mirrors DEFAULT. `EmbeddedView` renders `TotalSummaryCard` instead of
  // `Details`, so only `paymentContainer` is read on mobile — the `details*`
  // keys exist for the desktop-resize fallback, where `EmbeddedView` hands
  // off to `DefaultView` and the DEFAULT hero layout must measure correctly.
  [TemplateType.EMBEDDED_VIEW]: {
    detailsContainer: TEMPLATE_CLASSES.detailsContainerDefault,
    detailsWrapper: { mobile: '', desktop: TEMPLATE_CLASSES.detailsWrapperDesktopHalfColumn },
    detailsBodyStack: TEMPLATE_CLASSES.detailsBodyStackDefault,
    detailsCollapsible: {
      open: TEMPLATE_CLASSES.collapsibleOpen,
      closed: TEMPLATE_CLASSES.collapsibleClosed,
      content: TEMPLATE_CLASSES.collapsibleContentStandard,
    },
    paymentContainer: TEMPLATE_CLASSES.paymentContainerEmbeddedView,
  },
};

export interface TemplateConfig {
  type: TemplateType;
  features: TemplateFeatures;
  styles: TemplateStyles;
  backgroundImageUrl?: string;
}

/**
 * Builds the runtime {@link TemplateConfig} for a given template type.
 *
 * Note: this used to accept (and propagate) an `onePaymentMethod` arg
 * sourced from the pre-PR-#210 BE wire (`template: { type, onePaymentMethod }`).
 * The field was never read by any downstream consumer — the single
 * payment method is resolved exclusively by `SinglePaymentTemplate`'s
 * own `validatePreRender` / `filterAvailablePaymentMethods` /
 * `resolveDefaultPaymentMethod` from
 * `bootData.checkoutConfiguration.availablePaymentMethods`. Removed so
 * no one mistakes it for an active branch.
 */
export const getTemplateConfig = (templateType: TemplateType, backgroundImageUrl?: string): TemplateConfig => {
  return {
    type: templateType,
    features: TemplateConfigurations[templateType],
    styles: TemplateStyleConfigurations[templateType],
    backgroundImageUrl,
  };
};

/**
 * Placeholder hero shipped by the BE when a publisher hasn't uploaded their
 * own background. It's not a real design asset, so we treat it as "no image":
 * no hero is painted and text auto-contrasts to black.
 */
export const DEFAULT_BACKGROUND_IMAGE_URL = 'https://media.appcharge.com/defaults/background.png';

export const BLACK_COLOR = '#000000';
export const WHITE_COLOR = '#ffffff';

export const VALID_DETAILS_CONTENT_COLORS = [BLACK_COLOR, WHITE_COLOR];
